// src/context/LocationContext.js

/**
 * Global Location container
 *  Must wrap the whole app so that the location
 *  can be accessed anywhere in the app.
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { devLog, logError } from "../utils/logger";

/**
 * The Context API creates a provider-consumer relationship
 * Creating an empty container (The context container)
 *  Will hold location data
 */
const LocationContext = createContext();

/**
 * Creating the provider of the context
 * Managing the content of the container
 *  Holds all location states
 *  Provides functions to request location
 *  Wraps the app to make location data available everywhere
 */
export function LocationProvider({ children }) {
  /**
   * State to hold the current user's location
   */
  const [userLocation, setUserLocation] = useState(null);
  /**
   * State to indicate Location error
   */
  const [locationError, setLocationError] = useState(null);
  /**
   * State to indicate that location is loading
   */
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Track last request time to use for rate limiting
   */
  const lastRequestTime = useRef(null);

  /**
   * Function to request the current location with smart rate limiting
   */
  const requestLocation = useCallback(
    async (maxAge = 60000) => {
      const now = Date.now();

      /**
       * Check if current location time stamp is not older than the maxAge
       * if location age is less than max age, return the cached location
       */
      if (userLocation?.timestamp) {
        const locationAge = now - userLocation.timestamp;
        if (locationAge < maxAge) {
          devLog(
            `Using cached location (${Math.round(locationAge / 1000)}s old, max: ${maxAge / 1000}s)`,
          );
          return userLocation;
        }
      }

      /**
       * The location has just been requested and it is loading when another request comes in.
       * This case handles that. return the same location
       */
      if (isLoading) {
        devLog("Location request in progress, returning current location");
        return userLocation;
      }

      /**
       * Limit GPS requests to have a 2 seconds interval (2 second minimum between actual GPS calls)
       * If requests have less than a 2 sec interval, return the cached location
       */
      const timeSinceLastRequest = lastRequestTime.current
        ? now - lastRequestTime.current
        : Infinity;
      if (timeSinceLastRequest < 2000) {
        devLog(
          "GPS request rate limited, returning current location if available",
        );
        return userLocation;
      }

      devLog(
        `Requesting fresh location (current age: ${userLocation?.timestamp ? Math.round((now - userLocation.timestamp) / 1000) : "none"}s, max age: ${maxAge / 1000}s)`,
      );

      /**
       * Clear any existing errors and start loading
       */
      setLocationError(null);
      setIsLoading(true);

      lastRequestTime.current = now;

      /**
       * Check if current browser supports geolocation
       */
      if (!navigator.geolocation) {
        const error = {
          type: "location_unsupported",
          message: "Geolocation is not supported by this browser.",
          retryAction: null,
        };
        setLocationError(error);
        setIsLoading(false);
        /**
         * At this point, userLocation would be null -> MapboxMap will not render in Home.js
         * That is okay because LocationError has already been set -> Error Modal will render
         */
        return userLocation;
      }

      /**
       * We are making the GPS call asynchronous, we are creating a new promise
       *  a promise takes an "executor" function an argument. (a function to execute)
       *    The executor in this case takes in a resolve and reject function call back to execute if operation succeeds of fails.
       *    getCurrentPosition will call resolve if it successful in obtaining a location -> The promise returns a value
       *    getCurrentPosition will call reject if it fails in obtaining a location -> This throws an error (caught by the catch block)
       */
      try {
        const position = await new Promise((resolve, reject) => {
          /**
           * originalGeolocation is basically window.navigator.geolocation
           * We are using it here because in <MapboxMap>, we replace window.navigator.geolocation with our mock geolocation
           * We want real GPS data here, that is why
           */
          if (!window.originalGeolocation) {
            const error = new Error(
              "Original geolocation API not available - may have been replaced by mock",
            );
            error.code = "ORIGINAL_GEOLOCATION_UNAVAILABLE";
            reject(error);
            return;
          }

          window.originalGeolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            /**
             * Use the requested maxAge but cap at 1 minute
             */
            maximumAge: Math.min(maxAge, 60000),
          });
        });

        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        };

        devLog("Location obtained:", location);
        setUserLocation(location);
        setLocationError(null);
        setIsLoading(false);

        return location;
      } catch (error) {
        logError("Location error:", error);

        let locationError;
        if (error.code === "ORIGINAL_GEOLOCATION_UNAVAILABLE") {
          locationError = {
            type: "geolocation_api_unavailable",
            message:
              "Location services are temporarily unavailable. Please refresh the page and try again.",
            retryAction: null,
          };
        } else if (error.code === error.PERMISSION_DENIED) {
          locationError = {
            type: "location_permission",
            message:
              "Location access denied. Please enable location access and try again.",
            retryAction: () => requestLocation(maxAge),
          };
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          locationError = {
            type: "location_unavailable",
            message:
              "Location information is unavailable. Please check your connection and try again.",
            retryAction: () => requestLocation(maxAge),
          };
        } else if (error.code === error.TIMEOUT) {
          locationError = {
            type: "location_timeout",
            message: "Location request timed out. Please try again.",
            retryAction: () => requestLocation(maxAge),
          };
        } else {
          locationError = {
            type: "location_unknown",
            message: `Failed to get location: ${error.message}`,
            retryAction: () => requestLocation(maxAge),
          };
        }

        setLocationError(locationError);
        setIsLoading(false);
        return null;
      }
    },
    [userLocation, isLoading],
  );

  /**
   * Creating values that will be available to any components inside the provider
   */
  const value = {
    userLocation,
    setUserLocation,
    locationError,
    setLocationError,
    isLoading,
    requestLocation,
    // Utility function to clear location data
    clearLocation: useCallback(() => {
      setUserLocation(null);
      setLocationError(null);
      setIsLoading(false);
      lastRequestTime.current = null;
    }, []),
  };

  /**
   * Making the value available to all the children wrapped within this provider component
   */
  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

/**
 * Custom hook to use within wrapped children components to get provider values (created in the LocationProvider function)
 * Called by the consumer of the context
 */
export function useLocation() {
  /**
   * This will find the closest component ancestor with a matching provider name (LocationContext)
   *  Once the provider name is found, React returns whatever was passed into the provider's value prop
   */
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error(
      "No matching provider found. useLocation must be used within a LocationProvider",
    );
  }
  return context;
}
